import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'ja' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 翻訳データ
const translations: Record<Language, Record<string, string>> = {
  ja: {
    // 共通
    'common.home': 'ホーム',
    'common.back': '戻る',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.edit': '編集',
    'common.delete': '削除',
    'common.loading': '読み込み中...',
    'common.error': 'エラー',
    'common.success': '成功',
    
    // プロフィール
    'profile.title': 'マイページ',
    'profile.displayName': '表示名',
    'profile.email': 'メールアドレス',
    'profile.phoneNumber': '電話番号',
    'profile.company': '会社名',
    'profile.age': '年齢',
    'profile.accountCreated': 'アカウント作成日',
    'profile.notSet': '未設定',
    'profile.unknown': '不明',
    'profile.editProfile': 'プロフィール編集',
    'profile.updateSuccess': 'プロフィールを更新しました',
    'profile.updateError': 'プロフィールの更新に失敗しました',
    'profile.fetchError': 'プロフィール情報の取得に失敗しました',
    
    // 設定
    'settings.title': '設定',
    'settings.language': '言語設定',
    'settings.language.japanese': '日本語',
    'settings.language.english': 'English',
    'settings.notifications': '通知設定',
    'settings.privacy': 'プライバシー設定',
    'settings.account': 'アカウント設定',
    
    // 認証
    'auth.login': 'ログイン',
    'auth.logout': 'ログアウト',
    'auth.signup': '新規登録',
    'auth.email': 'メールアドレス',
    'auth.password': 'パスワード',
    'auth.confirmPassword': 'パスワード確認',
    'auth.forgotPassword': 'パスワードを忘れた方',
    'auth.resetPassword': 'パスワードリセット',
    'auth.loginError': 'ログインに失敗しました',
    'auth.logoutError': 'ログアウトに失敗しました',
    'auth.signupError': '新規登録に失敗しました',
    
    // 写真管理
    'photo.upload': '写真アップロード',
    'photo.gallery': '写真ギャラリー',
    'photo.edit': '写真編集',
    'photo.delete': '写真削除',
    'photo.select': '写真選択',
    'photo.bulkOperation': '一括操作',
    'photo.search': '写真検索',
    
    // 現場管理
    'site.create': '現場作成',
    'site.edit': '現場編集',
    'site.delete': '現場削除',
    'site.name': '現場名',
    'site.address': '住所',
    'site.progress': '進捗',
    'site.members': 'メンバー',
    
    // レポート
    'report.generate': 'レポート生成',
    'report.download': 'ダウンロード',
    'report.print': '印刷',
    'report.pdf': 'PDF出力',
    'report.excel': 'Excel出力',
    
    // 通知
    'notification.title': '通知',
    'notification.markAsRead': '既読にする',
    'notification.delete': '通知削除',
    'notification.noNotifications': '通知はありません',
    
    // エラー
    'error.network': 'ネットワークエラーが発生しました',
    'error.permission': '権限がありません',
    'error.notFound': 'ページが見つかりません',
    'error.server': 'サーバーエラーが発生しました',
  },
  en: {
    // Common
    'common.home': 'Home',
    'common.back': 'Back',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    
    // Profile
    'profile.title': 'My Page',
    'profile.displayName': 'Display Name',
    'profile.email': 'Email Address',
    'profile.phoneNumber': 'Phone Number',
    'profile.company': 'Company',
    'profile.age': 'Age',
    'profile.accountCreated': 'Account Created',
    'profile.notSet': 'Not Set',
    'profile.unknown': 'Unknown',
    'profile.editProfile': 'Edit Profile',
    'profile.updateSuccess': 'Profile updated successfully',
    'profile.updateError': 'Failed to update profile',
    'profile.fetchError': 'Failed to fetch profile information',
    
    // Settings
    'settings.title': 'Settings',
    'settings.language': 'Language Settings',
    'settings.language.japanese': '日本語',
    'settings.language.english': 'English',
    'settings.notifications': 'Notification Settings',
    'settings.privacy': 'Privacy Settings',
    'settings.account': 'Account Settings',
    
    // Auth
    'auth.login': 'Login',
    'auth.logout': 'Logout',
    'auth.signup': 'Sign Up',
    'auth.email': 'Email Address',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.forgotPassword': 'Forgot Password?',
    'auth.resetPassword': 'Reset Password',
    'auth.loginError': 'Login failed',
    'auth.logoutError': 'Logout failed',
    'auth.signupError': 'Sign up failed',
    
    // Photo Management
    'photo.upload': 'Upload Photo',
    'photo.gallery': 'Photo Gallery',
    'photo.edit': 'Edit Photo',
    'photo.delete': 'Delete Photo',
    'photo.select': 'Select Photo',
    'photo.bulkOperation': 'Bulk Operation',
    'photo.search': 'Search Photos',
    
    // Site Management
    'site.create': 'Create Site',
    'site.edit': 'Edit Site',
    'site.delete': 'Delete Site',
    'site.name': 'Site Name',
    'site.address': 'Address',
    'site.progress': 'Progress',
    'site.members': 'Members',
    
    // Reports
    'report.generate': 'Generate Report',
    'report.download': 'Download',
    'report.print': 'Print',
    'report.pdf': 'Export PDF',
    'report.excel': 'Export Excel',
    
    // Notifications
    'notification.title': 'Notifications',
    'notification.markAsRead': 'Mark as Read',
    'notification.delete': 'Delete Notification',
    'notification.noNotifications': 'No notifications',
    
    // Errors
    'error.network': 'Network error occurred',
    'error.permission': 'Permission denied',
    'error.notFound': 'Page not found',
    'error.server': 'Server error occurred',
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('ja');

  // ローカルストレージから言語設定を読み込み
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'ja' || savedLanguage === 'en')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  // 言語設定を変更
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  // 翻訳関数
  const t = (key: string): string => {
    return (translations[language] as Record<string, string>)[key] || key;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// カスタムフック
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 